import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCode, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import DrawerRowDivider from "../../../../components/drawer/DrawerRowDivider";
import Ln2MappingEditor, {
  defaultLn2MappingData,
} from "../../../../components/adapters/Ln2MappingEditor";
import LobbyVision2TelegraphFields, {
  LobbyVision2AddressingPanel,
  LobbyVision2ExpansionToggles,
  LobbyVision2RowLiftNumOverride,
  REQUEST_FRAME_TYPES,
  RESPONSE_FRAME_TYPES,
  buildLobbyVision2ExpansionMessage,
} from "./LobbyVision2MappingRow";
// INTERFACES
import type {
  AdapterCdoLobbyVision2,
  LobbyVision2ReadRow,
  LobbyVision2WriteRow,
} from "../../../../../interfaces/AdapterCdoLobbyVision2.interface";
import {
  blankLobbyVision2ReadRow,
  blankLobbyVision2WriteRow,
  ccTypeForCallModeChange,
  isLobbyVision2CallLn2Message,
  lobbyVision2RowCallTypeValid,
  lobbyVision2RowNeedsCallType,
} from "../../../../../interfaces/AdapterCdoLobbyVision2.interface";
import { RootState } from "../../../../../app/store";

const { Panel } = Collapse;

/** One-line row summary: telegraph op + toggle state (mirrors old bank_num/lift_num describeTelegraph, now toggle-based since the template is bank/lift-agnostic). */
function describeTelegraph(
  telegraphType: number,
  unitsBased: boolean,
  floorsBased: boolean,
  bankLevel: boolean,
  individual: boolean,
): string {
  return `(frame=${telegraphType}, unit=${unitsBased}, floor=${floorsBased}, bank=${bankLevel}, own=${individual})`;
}

/** LobbyVision 2 CDO editor: per-adapter addressing config + Writes (LN2 → Telegraph) and Reads (Telegraph → LN2) template tables. */
export default function CdoLobbyVision2Page({
  setCdo,
  cdoValue,
}: {
  setCdo: (newValue: AdapterCdoLobbyVision2) => void;
  cdoValue: AdapterCdoLobbyVision2;
}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);

  const [draftWrite, setDraftWrite] = useState<LobbyVision2WriteRow>(
    blankLobbyVision2WriteRow(defaultLn2MappingData()),
  );
  const [draftRead, setDraftRead] = useState<LobbyVision2ReadRow>(
    blankLobbyVision2ReadRow(defaultLn2MappingData()),
  );

  // The LobbyVision2 template is bank/lift-agnostic: an entry only needs a
  // telegraph op + LN2 message + expansion toggles. LiftCloud expands the
  // template per-car/per-floor at CDO-generation time, deriving lift_num/
  // bank_num/data from the adapter's addressing config + the assigned bank.
  // A row is addable once it has an LN2 message and — when it is a call mapping
  // (a call operationType OR a call LN2 message) — a chosen SecureCarCalltype.
  const canAddWrite =
    draftWrite.message.message !== null && lobbyVision2RowCallTypeValid(draftWrite);
  const canAddRead = draftRead.message.message !== null && lobbyVision2RowCallTypeValid(draftRead);

  // Whether each draft's mapped LN2 message is itself a call — drives the
  // SecureCarCalltype field / Floor exclusion even for a non-call operationType.
  const draftWriteMessageIsCall = isLobbyVision2CallLn2Message(draftWrite.message.message);
  const draftReadMessageIsCall = isLobbyVision2CallLn2Message(draftRead.message.message);

  // Apply a new LN2 message to a draft, resetting ccType if call-mode flips
  // (mirrors what the operationType onChange does, for the message half of
  // the trigger). `data` is independent of call-mode and is left untouched.
  const onChangeWriteMessage = (message: LobbyVision2WriteRow["message"]) => {
    const next = { ...draftWrite, message };
    setDraftWrite({
      ...next,
      ccType: ccTypeForCallModeChange(
        lobbyVision2RowNeedsCallType(draftWrite),
        lobbyVision2RowNeedsCallType(next),
        draftWrite.ccType,
      ),
    });
  };
  const onChangeReadMessage = (message: LobbyVision2ReadRow["message"]) => {
    const next = { ...draftRead, message };
    setDraftRead({
      ...next,
      ccType: ccTypeForCallModeChange(
        lobbyVision2RowNeedsCallType(draftRead),
        lobbyVision2RowNeedsCallType(next),
        draftRead.ccType,
      ),
    });
  };

  const onChangeAddressing = (addressing: AdapterCdoLobbyVision2["addressing"]) => {
    setCdo({ ...cdoValue, addressing });
  };

  const onAddWrite = () => {
    if (!canAddWrite) return;
    setCdo({ ...cdoValue, writes: [...cdoValue.writes, draftWrite] });
    setDraftWrite(blankLobbyVision2WriteRow(defaultLn2MappingData()));
  };

  const onAddRead = () => {
    if (!canAddRead) return;
    setCdo({ ...cdoValue, reads: [...cdoValue.reads, draftRead] });
    setDraftRead(blankLobbyVision2ReadRow(defaultLn2MappingData()));
  };

  const onRemoveWrite = (index: number) => {
    setCdo({ ...cdoValue, writes: cdoValue.writes.filter((_, i) => i !== index) });
  };

  const onRemoveRead = (index: number) => {
    setCdo({ ...cdoValue, reads: cdoValue.reads.filter((_, i) => i !== index) });
  };

  return (
    <div>
      <DrawerRowDivider icon={faFileCode} title="LobbyVision 2" />
      <div className={`drawer__box__${theme}`}>
        {/* PER-ADAPTER ADDRESSING CONFIG (rendered once, above both mapping tables) */}
        <LobbyVision2AddressingPanel value={cdoValue.addressing} onChange={onChangeAddressing} />

        {/* LN2 -> TELEGRAPH (outbound: an LN2 command becomes a vendor request telegraph) */}
        <Collapse defaultActiveKey={["writes"]}>
          <Panel
            header={t("general.lobbyVision2WriteMappings", { total: cdoValue.writes.length })}
            key="writes"
          >
            {cdoValue.writes.map((row, index) => (
              <div
                key={`write-${index}`}
                className="mb-3 generalStyles__flex"
                style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
              >
                <div style={{ width: "100%" }}>
                  <div className="generalStyles__textEllipsis">
                    {row.message.message?.label ?? "message"} →{" "}
                    {describeTelegraph(
                      row.telegraphType,
                      row.unitsBased,
                      row.floorsBased,
                      row.bankLevel,
                      row.individual,
                    )}
                  </div>
                </div>
                <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                  <Tooltip title="Delete">
                    <Button
                      onClick={() => onRemoveWrite(index)}
                      className="buttonStyle__29"
                      shape="circle"
                      icon={<FontAwesomeIcon icon={faTrash} />}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
            <Ln2MappingEditor
              value={draftWrite.message}
              onChange={onChangeWriteMessage}
              hideBankLift
            />
            <LobbyVision2TelegraphFields
              value={draftWrite}
              onChange={(patch) => setDraftWrite({ ...draftWrite, ...patch })}
              allowedFrameTypes={REQUEST_FRAME_TYPES}
              floorsBased={draftWrite.floorsBased}
              messageIsCall={draftWriteMessageIsCall}
            />
            <LobbyVision2ExpansionToggles
              unitsBased={draftWrite.unitsBased}
              floorsBased={draftWrite.floorsBased}
              bankLevel={draftWrite.bankLevel}
              individual={draftWrite.individual}
              onChange={(patch) => setDraftWrite({ ...draftWrite, ...patch })}
            />
            <LobbyVision2RowLiftNumOverride
              individual={draftWrite.individual}
              liftNumSourceOverride={draftWrite.liftNumSourceOverride}
              liftNumBaseOverride={draftWrite.liftNumBaseOverride}
              onChange={(patch) => setDraftWrite({ ...draftWrite, ...patch })}
            />
            {buildLobbyVision2ExpansionMessage(
              draftWrite.unitsBased,
              draftWrite.floorsBased,
              draftWrite.bankLevel,
              draftWrite.individual,
            ) && (
              <div className="row mt-2">
                <div className="col-12" style={{ opacity: 0.6, fontSize: 12 }}>
                  {buildLobbyVision2ExpansionMessage(
                    draftWrite.unitsBased,
                    draftWrite.floorsBased,
                    draftWrite.bankLevel,
                    draftWrite.individual,
                  )}
                </div>
              </div>
            )}
            <div className="row mt-2">
              <div className="col-12 col-md-3">
                <Button
                  onClick={onAddWrite}
                  disabled={!canAddWrite}
                  className={`generalStyles__width100 ${canAddWrite ? "buttonStyle__28" : ""}`}
                >
                  <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                  Add
                </Button>
              </div>
            </div>
          </Panel>
        </Collapse>
        <br />
        {/* TELEGRAPH -> LN2 (inbound: a vendor response telegraph becomes an LN2 message) */}
        <Collapse>
          <Panel
            header={t("general.lobbyVision2ReadMappings", { total: cdoValue.reads.length })}
            key="reads"
          >
            {cdoValue.reads.map((row, index) => (
              <div
                key={`read-${index}`}
                className="mb-3 generalStyles__flex"
                style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
              >
                <div style={{ width: "100%" }}>
                  <div className="generalStyles__textEllipsis">
                    {describeTelegraph(
                      row.telegraphType,
                      row.unitsBased,
                      row.floorsBased,
                      row.bankLevel,
                      row.individual,
                    )}{" "}
                    → {row.message.message?.label ?? "message"}
                  </div>
                </div>
                <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                  <Tooltip title="Delete">
                    <Button
                      onClick={() => onRemoveRead(index)}
                      className="buttonStyle__29"
                      shape="circle"
                      icon={<FontAwesomeIcon icon={faTrash} />}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
            <LobbyVision2TelegraphFields
              value={draftRead}
              onChange={(patch) => setDraftRead({ ...draftRead, ...patch })}
              allowedFrameTypes={RESPONSE_FRAME_TYPES}
              floorsBased={draftRead.floorsBased}
              messageIsCall={draftReadMessageIsCall}
            />
            <LobbyVision2ExpansionToggles
              unitsBased={draftRead.unitsBased}
              floorsBased={draftRead.floorsBased}
              bankLevel={draftRead.bankLevel}
              individual={draftRead.individual}
              onChange={(patch) => setDraftRead({ ...draftRead, ...patch })}
            />
            <LobbyVision2RowLiftNumOverride
              individual={draftRead.individual}
              liftNumSourceOverride={draftRead.liftNumSourceOverride}
              liftNumBaseOverride={draftRead.liftNumBaseOverride}
              onChange={(patch) => setDraftRead({ ...draftRead, ...patch })}
            />
            {buildLobbyVision2ExpansionMessage(
              draftRead.unitsBased,
              draftRead.floorsBased,
              draftRead.bankLevel,
              draftRead.individual,
            ) && (
              <div className="row mt-2">
                <div className="col-12" style={{ opacity: 0.6, fontSize: 12 }}>
                  {buildLobbyVision2ExpansionMessage(
                    draftRead.unitsBased,
                    draftRead.floorsBased,
                    draftRead.bankLevel,
                    draftRead.individual,
                  )}
                </div>
              </div>
            )}
            <Ln2MappingEditor
              value={draftRead.message}
              onChange={onChangeReadMessage}
              hideBankLift
            />
            <div className="row mt-2">
              <div className="col-12 col-md-3">
                <Button
                  onClick={onAddRead}
                  disabled={!canAddRead}
                  className={`generalStyles__width100 ${canAddRead ? "buttonStyle__28" : ""}`}
                >
                  <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                  Add
                </Button>
              </div>
            </div>
          </Panel>
        </Collapse>
      </div>
    </div>
  );
}
